import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { API_BASE_URL } from '../api-base-url.token';

export interface SupervisionHeader {
  patientsPresents: number;
  enAttente: number;
  evolutionAttente: number;
  tempsAttenteMoyen: number;
  chargeCapacite: number;
  tension: 'stable' | 'modérée' | 'critique';
  satisfaction: number;
  debitGlobal: number;
  tempsCyclePatient: number;
}

export interface SupervisionAlert {
  level: 'vert' | 'orange' | 'rouge';
  titre: string;
  diagnostic: string;
  tendanceFile: string;
  debordementEstime: number | null;
  actionRecommandee: string;
  depuis: string;
}

export interface FluxEntry {
  time: string;
  entrees: number;
  sorties: number;
  capaciteMax: number;
}

export interface SupervisionFlux {
  entries: FluxEntry[];
  accumulation: number;
  tendance: string;
}

export interface SalleProduction {
  nom: string;
  debit: number;
  occupation: number;
  tempsMoyenPatient: number;
  impactGlobal: string | null;
  anomalie: string | null;
}

export interface SupervisionProduction {
  salles: SalleProduction[];
  performanceStaff: number;
  tendanceStaff: number;
}

export interface SupervisionPrediction {
  tempsAvantDebordement: number | null;
  saturationEstimee: number;
  projectionAttente: number;
  picAVenir: string;
  tempsRetourNormal: number | null;
}

export interface SupervisionAction {
  id: string;
  label: string;
  icon: string;
  impact: string;
  type: string;
  prioritaire: boolean;
}

export interface SupervisionSnapshot {
  header: SupervisionHeader;
  alert: SupervisionAlert;
  flux: SupervisionFlux;
  production: SupervisionProduction;
  prediction: SupervisionPrediction;
  actions: SupervisionAction[];
}

const MOCK: SupervisionSnapshot = {
  header: {
    patientsPresents: 28,
    enAttente: 14,
    evolutionAttente: 2,
    tempsAttenteMoyen: 18,
    chargeCapacite: 120,
    tension: 'modérée',
    satisfaction: 3.6,
    debitGlobal: 18,
    tempsCyclePatient: 42,
  },
  alert: {
    level: 'rouge',
    titre: 'Saturation en cours',
    diagnostic: '+10 patients en attente — Capacité insuffisante',
    tendanceFile: 'File augmente depuis 15 min',
    debordementEstime: 12,
    actionRecommandee: 'Ouvrir salle C',
    depuis: '15 min',
  },
  flux: {
    entries: [],
    accumulation: 10,
    tendance: '+4 patients / 10 min',
  },
  production: {
    salles: [
      { nom: 'Salle A', debit: 4, occupation: 80, tempsMoyenPatient: 12, impactGlobal: null, anomalie: null },
      { nom: 'Salle B', debit: 2, occupation: 95, tempsMoyenPatient: 24, impactGlobal: '+6 min attente globale', anomalie: 'Lenteur anormale' },
      { nom: 'Salle C', debit: 3, occupation: 55, tempsMoyenPatient: 14, impactGlobal: null, anomalie: null },
    ],
    performanceStaff: 74,
    tendanceStaff: -3,
  },
  prediction: {
    tempsAvantDebordement: 12,
    saturationEstimee: 88,
    projectionAttente: 28,
    picAVenir: '15:30',
    tempsRetourNormal: 35,
  },
  actions: [
    { id: '1', label: 'Ouvrir salle C', icon: '🧪', impact: '-9 min attente', type: 'ouvrir-salle', prioritaire: true },
    { id: '2', label: 'Renfort accueil', icon: '👥', impact: '-5 min attente', type: 'envoyer-renfort', prioritaire: false },
    { id: '3', label: 'Prioriser prélèvements', icon: '⚡', impact: 'Stabilisation flux', type: 'prioriser-flux', prioritaire: false },
  ],
};

@Injectable({ providedIn: 'root' })
export class SupervisionService {
  private readonly http = inject(HttpClient);
  private readonly baseRoot = inject(API_BASE_URL).replace(/\/+$/, '');
  private readonly base = `${this.baseRoot}/api/v1/supervision`;

  private readonly MOCK_MODE = true;

  get isMockMode(): boolean { return this.MOCK_MODE; }

  getSnapshot(establishmentId: string): Observable<SupervisionSnapshot> {
    if (this.MOCK_MODE) return of(MOCK);
    return this.http.get<SupervisionSnapshot>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/snapshot`
    );
  }

  executeAction(establishmentId: string, action: string): Observable<{ success: boolean; message: string }> {
    if (this.MOCK_MODE) return of({ success: true, message: `Action "${action}" exécutée` });
    return this.http.post<{ success: boolean; message: string }>(
      `${this.base}/establishments/${encodeURIComponent(establishmentId)}/actions/${action}`,
      {}
    );
  }
}
